<template>
  <div class="reservation-addition-and-upgrade flex flex-col gap-5">
    <div v-if="upgradeItems.length" class="upgrades">
      <p class="label text-black-50 py-4">{{ $t('product_selection_title') }}</p>
      <template v-if="isRadioUpgradeMode">
        <n-radio-group v-model:value="selectedUpgrade[0]" class="w-full" @update:value="handleUpgradeChange">
          <template v-for="u in upgradeUiItems" :key="u.name">
            <div class="item py-2 border-b-1 border-gray-40">
              <div class="flex justify-between items-start py-2" @click.capture="handleUpgradeClick($event, u)">
                <n-radio :class="{ readonly: u.isRequired }" :value="u.upgrade_id" :disabled="u.disabled" :label="u.name" />
                <span class="price-label whitespace-nowrap" :class="[u.disabled && 'opacity-50']">{{ u.priceLabel }}</span>
              </div>
              <p v-if="u.description" class="ml-7 mb-2 text-sm text-gray-80">
                {{ u.description }}
              </p>
              <p v-if="u.isRequired" class="ml-7 mb-2 text-yellow-50 text-sm">
                {{ $t('upgrade_must_book') }}
              </p>
              <div v-if="isAllowMulti && selectedUpgrade[0] === u.upgrade_id" class="counter flex justify-end mt-2 -mr-2">
                <nd-number-counter
                  :min="u.limit_min"
                  :max="u.maxCount"
                  :value="upgradeResource[u.upgrade_id]"
                  @update:value="handleUpgradeCountChange(u.upgrade_id, $event)"
                ></nd-number-counter>
              </div>
            </div>
          </template>
        </n-radio-group>
      </template>
      <template v-else>
        <n-checkbox-group v-model:value="selectedUpgrade" class="w-full" @update:value="handleUpgradeChange">
          <template v-for="u in upgradeUiItems" :key="u.name">
            <div class="item py-2 border-b-1 border-gray-40">
              <div class="flex justify-between items-start py-2" @click.capture="handleUpgradeClick($event, u)">
                <n-checkbox :class="{ readonly: u.isRequired }" :value="u.upgrade_id" :disabled="u.disabled" :label="u.name" />
                <span class="price-label whitespace-nowrap" :class="[u.disabled && 'opacity-50']">{{ u.priceLabel }}</span>
              </div>
              <p v-if="u.description" class="ml-7 mb-2 text-sm text-gray-80">
                {{ u.description }}
              </p>
              <p v-if="u.isRequired" class="ml-7 mb-2 text-yellow-50 text-sm">
                {{ $t('upgrade_must_book') }}
              </p>
              <div v-if="isAllowMulti && selectedUpgrade.includes(u.upgrade_id)" class="counter flex justify-end mt-2 -mr-2">
                <nd-number-counter
                  :min="u.limit_min"
                  :max="u.maxCount"
                  :value="upgradeResource[u.upgrade_id]"
                  @update:value="handleUpgradeCountChange(u.upgrade_id, $event)"
                ></nd-number-counter>
              </div>
            </div>
          </template>
        </n-checkbox-group>
      </template>
    </div>
    <div v-if="additionItems.length" class="additions">
      <p class="label text-black-50 py-4">{{ $t('package_add_on_title') }}</p>
      <n-checkbox-group v-model:value="selectedAddition" class="w-full" @update:value="handleAdditionChange">
        <template v-for="a in additionItems" :key="a.name">
          <div class="item py-2 border-b-1 border-gray-40">
            <div class="flex justify-between items-start py-2">
              <n-checkbox :value="a.product_id" :label="a.name" />
              <span class="price-label whitespace-nowrap">+ {{ product.currency_code }} {{ priceFormat(a.discount!) }}</span>
            </div>
            <div v-if="a.count && a.count > 1 && selectedAddition.includes(a.product_id || -1)" class="counter flex justify-end mt-2 -mr-2">
              <nd-number-counter
                :max="a.count"
                :value="additionResource[a.product_id!]"
                @update:value="handleAdditionCountChange(a.product_id!, $event)"
              ></nd-number-counter>
            </div>
          </div>
        </template>
      </n-checkbox-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NCheckbox, NCheckboxGroup, NRadio, NRadioGroup } from 'naive-ui'
import type { MaybeArray } from 'naive-ui/es/_utils'

import NdNumberCounter from '@/components/form/nd-number-counter.vue'
import { priceFormat } from '@/utils/price'

import { getProductAdditionAndUpgrade } from '@/service/product'

import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import type { ProductUpgradeResource, ProductAdditionsResource } from '@/types/booking'
import type { FproductAddUpgUpgradeData, FproductAddUpgAdditionData } from '@/types/api'
import { EdbProductUpgradeMode as ApiEdbProductUpgradeMode, FproductLimitFlag as ApiFproductLimitFlag } from '@/types/api'

const DEFAULT_MAX_SUM = 99

const { t } = useI18n()

const route = useRoute()
// import { useCurrencyStore } from '@/store/currency'
// const currencyStore = useCurrencyStore()
// const decimalPrecision = computed(() => currencyStore.currencyMapByCode[props.product?.currency_code || '']?.decimal_precision || 0)

const props = defineProps<{
  product: UiProductInfo
  bookingItem: UiProductSelectedData[]
}>()

const { regionId } = useRegion()

const productId = computed(() => `${route.params.pid}`)

const bookingItemTotalPrice = computed(() => {
  return props.bookingItem.reduce((acc, item) => acc + item.price, 0)
})
const bookingItemOriginTotalPrice = computed(() => props.bookingItem.reduce((acc, item) => acc + item.originPrice, 0))

/** upgrade */
const upgrades = defineModel<ProductUpgradeResource[]>('upgrades', { default: [] })
const selectedUpgrade = ref<number[]>([])
const upgradeResource = ref<Record<number, number>>({})

const { data: productAdditionAndUpgrade, execute: fetchProductAdditionAndUpgrade } = await useAsyncData(
  `product_addition_and_upgrade_${productId.value}_${props.bookingItem.map(item => item.dateTime).join(',')}`,
  async () => {
    if (!productId.value || !props.bookingItem.length) return null

    const arrivalTime = props.bookingItem?.[0]?.dateTime
    const queryParams = { sale_region_id: regionId.value || 0, arrival_time: arrivalTime || '' }
    const { data: productAdditionAndUpgrade } = await getProductAdditionAndUpgrade(productId.value, queryParams)
    return productAdditionAndUpgrade
  },
  { watch: [productId, props.bookingItem] }
)
watch(
  () => props.bookingItem,
  async () => {
    await fetchProductAdditionAndUpgrade()
    initSelectedUpgrade()
    initSelectedAddition()
  },
  { deep: true, immediate: true }
)

const isAllowMulti = computed(() => productAdditionAndUpgrade.value?.allow_multi)
const isRadioUpgradeMode = computed(() => productAdditionAndUpgrade.value?.upgrade_mode === ApiEdbProductUpgradeMode.ProductUpgradeModeSingle)

const defaultUpgradeObj = computed<FproductAddUpgUpgradeData>(() => ({
  upgrade_id: -1,
  bid: props.product?.bid,
  description: '',
  duration: 0,
  name: props.product?.product_name,
  price: 0,
  origin_price: 0,
  resource_use: productAdditionAndUpgrade.value?.resource_use,
  limit_min: 1,
  is_sold_out: false,
  is_required: false,
}))
const upgradeItems = computed(() => {
  if (Array.isArray(productAdditionAndUpgrade.value?.upgrades) && productAdditionAndUpgrade.value?.upgrades?.length) {
    return productAdditionAndUpgrade.value.upgrades.map(item => ({ ...item, is_sold_out: item.is_sold_out ?? false }))
    // 可多選主方案數量無子選項，會在這選主方案數量。可多選主方案數量有子選項的話，會在前一頁選主方案數量
  } else if (productAdditionAndUpgrade.value?.allow_multi && !productAdditionAndUpgrade.value?.sub_items?.length) {
    return [defaultUpgradeObj.value]
  }
  return []
})
const upgradesMap = computed(() => {
  return upgradeItems.value.reduce(
    (acc, item) => {
      if (item?.upgrade_id) acc[item.upgrade_id] = item as FproductAddUpgUpgradeData
      return acc
    },
    {} as Record<number, FproductAddUpgUpgradeData>
  )
})
const upgradeUiItems = computed(() => {
  if (!productAdditionAndUpgrade.value) return []
  return upgradeItems.value.map(item => {
    const itemResourceUse = productAdditionAndUpgrade.value?.is_cust_num ? 1 : (item.resource_use ?? 1)
    const itemLimitMin = isNeedApplyLimitMin.value
      ? Math.max(productAdditionAndUpgrade.value?.limit_min || 0, item.limit_min || 0)
      : item.limit_min || 1
    const remainingCount =
      remainingResourceNum.value >= itemResourceUse && !isOverProductStock.value ? Math.floor(remainingResourceNum.value / itemResourceUse) : 0
    const description = (() => {
      if (item.is_sold_out) return t('all_inventory_status_fully_occupied')

      return item.duration ? t('package_upgrade_extend_duration', { extend_duration: item.duration }) : ''
    })()
    const upgradeId = item.upgrade_id || -1
    return {
      ...item,
      upgrade_id: upgradeId,
      limit_min: itemLimitMin,
      description,
      isRequired: item.is_required,
      priceLabel: `${props.product?.currency_code} ${priceFormat(getProductPrice(item.price))}`,
      disabled: isRadioUpgradeMode.value
        ? item.is_sold_out || (item?.limit_min || 0) > (productAdditionAndUpgrade.value?.limit_max || 0)
        : (!selectedUpgrade.value.includes(upgradeId) &&
            !item.is_required &&
            (remainingResourceNum.value < itemResourceUse * itemLimitMin || remainingStock.value < itemLimitMin)) ||
          item.is_sold_out,
      maxCount: remainingCount + (selectedUpgrade.value.includes(upgradeId) ? upgradeResource.value[upgradeId] || 0 : 0),
    }
  })
})
const requiredUpgradeIDs = computed(() => upgradeUiItems.value.filter(item => item.isRequired && !item.is_sold_out).map(item => item.upgrade_id))
const upgradesTotalCount = computed(() => Object.values(upgradeResource.value).reduce((acc, c) => acc + c, 0))

// 可選人數 & 有設定下限值 & ( 單一項目 或 單選模式 ) 就要套用下限值
const isNeedApplyLimitMin = computed(() => hasLimitMin.value && (upgradeItems.value.length === 1 || isRadioUpgradeMode.value))
const hasLimitMin = computed(
  () =>
    productAdditionAndUpgrade.value?.is_cust_num &&
    (productAdditionAndUpgrade.value?.limit_flag === ApiFproductLimitFlag.LimitFlagMin ||
      productAdditionAndUpgrade.value?.limit_flag === ApiFproductLimitFlag.LimitFlagBothMinAndMax)
)
const remainingStock = computed(() => {
  const result = (productAdditionAndUpgrade.value?.limit_max ?? DEFAULT_MAX_SUM) - upgradesTotalCount.value
  return result > 0 ? result : 0
})
const remainingResourceNum = computed(() => {
  const isCustomNumber = !!productAdditionAndUpgrade.value?.is_cust_num
  const maxResources = (isCustomNumber ? productAdditionAndUpgrade.value?.limit_max : productAdditionAndUpgrade.value?.max_sum) ?? DEFAULT_MAX_SUM
  const sumOccupiedResource = Object.keys(upgradeResource.value).reduce((sumOfResourceUsed, upgradeId) => {
    const id = Number(upgradeId)
    const resourceUse = upgradesMap.value?.[id]?.resource_use ?? 1
    return sumOfResourceUsed + (isCustomNumber ? 1 : resourceUse) * (upgradeResource.value[id] || 0)
  }, 0)
  return Math.max(maxResources - sumOccupiedResource, 0)
})

const isOverProductStock = computed(() => upgradesTotalCount.value >= (productAdditionAndUpgrade.value?.limit_max ?? DEFAULT_MAX_SUM))

const initSelectedUpgrade = () => {
  if (!productAdditionAndUpgrade.value) return

  if (isRadioUpgradeMode.value) {
    const selectTarget =
      upgradeUiItems.value.find(item => !item.disabled) || (upgradeUiItems.value[0] as (typeof upgradeUiItems.value)[0]) || defaultUpgradeObj.value
    selectedUpgrade.value[0] = selectTarget.upgrade_id || -1
    upgradeResource.value = { [selectedUpgrade.value[0]]: selectTarget.limit_min || 1 }
  } else {
    selectedUpgrade.value = selectedUpgrade.value.concat(requiredUpgradeIDs.value)
    upgradeResource.value = requiredUpgradeIDs.value.reduce(
      (acc, id) => {
        acc[id] = upgradesMap.value?.[id]?.limit_min || 1
        return acc
      },
      {} as Record<number, number>
    )
  }
}
const handleUpgradeClick = (event: MouseEvent, upgradeItem: (typeof upgradeUiItems.value)[0]) => {
  if (upgradeItem.disabled || upgradeItem.isRequired) {
    event.stopPropagation()
    event.preventDefault()
  }
}
const handleUpgradeChange = (upgradeIds: MaybeArray<number | string>) => {
  if (!Array.isArray(upgradeIds)) upgradeIds = [upgradeIds]
  upgradeResource.value = (upgradeIds as (string | number)[]).reduce(
    (acc, id) => {
      const numId = Number(id)
      acc[numId] = upgradeResource.value[numId] || upgradesMap.value?.[numId]?.limit_min || 1
      return acc
    },
    {} as Record<number, number>
  )
}
const handleUpgradeCountChange = (upgradeId: number, count: number) => {
  upgradeResource.value[upgradeId] = count
}

watch(
  upgradeResource,
  resources => {
    const result: ProductUpgradeResource[] = []
    // let totalAmount = 0 // 總金額
    let maxDuration = 0 // 最大延長時間

    for (const [upgradeID, upgradeCount] of Object.entries(resources)) {
      const id = Number(upgradeID)
      const upgradeObj = upgradesMap.value[id] || defaultUpgradeObj.value
      const price = getProductPrice(upgradeObj.price)
      const amount = Number(price) * upgradeCount
      const originPrice = getProductOriginPrice(upgradeObj.origin_price)
      const originAmount = Number(originPrice) * upgradeCount
      // totalAmount += Number(amount.toPrecision(decimalPrecision.value))

      result.push({
        upgrade_id: id,
        quantity: upgradeCount,
        count: upgradeCount,
        discount: price,
        amount: amount,
        name: upgradeObj.name,
        duration: (props.product?.duration || 0) + (upgradeObj.duration || 0),
        originAmount: originAmount || amount,
      })

      if (upgradeObj.duration) {
        maxDuration = Math.max(upgradeObj.duration, maxDuration)
      }
    }

    upgrades.value = result
  },
  { deep: true }
)

/** addition */
const additions = defineModel<ProductAdditionsResource[]>('additions', { default: [] })
const selectedAddition = ref<number[]>([])
const additionResource = ref<Record<number, number>>({})

const additionItems = computed(() => productAdditionAndUpgrade.value?.additions ?? [])
const additionsMap = computed(() => {
  return additionItems.value.reduce(
    (acc, item) => {
      if (item?.product_id) acc[item.product_id] = item
      return acc
    },
    {} as Record<number, FproductAddUpgAdditionData>
  )
})

const initSelectedAddition = () => {
  if (!productAdditionAndUpgrade.value) return

  selectedAddition.value = []
  additionResource.value = {}
}
const handleAdditionChange = (productIds: MaybeArray<number | string>) => {
  if (!Array.isArray(productIds)) productIds = [productIds]
  additionResource.value = (productIds as (string | number)[]).reduce(
    (acc, id) => {
      const numId = Number(id)
      acc[numId] = additionResource.value[numId] || 1
      return acc
    },
    {} as Record<number, number>
  )
}
const handleAdditionCountChange = (productId: number, count: number) => {
  additionResource.value[productId] = count
}

watch(
  additionResource,
  resources => {
    const result = []
    for (const [pid, additionCount] of Object.entries(resources)) {
      const id = Number(pid)
      const additionObj = additionsMap.value[id]
      if (!additionObj) continue
      const price = additionObj.discount
      const amount = Number(price) * additionCount
      const originPrice = additionObj.origin_price
      const originAmount = Number(originPrice) * additionCount
      result.push({
        pid: id,
        count: additionCount,
        discount: price,
        amount: amount,
        name: additionObj.name,
        originAmount: originAmount || amount,
      })
    }
    additions.value = result as ProductAdditionsResource[]
  },
  { deep: true }
)

/** util functions */
const getProductPrice = (upgradePrice = 0) => bookingItemTotalPrice.value + upgradePrice
const getProductOriginPrice = (upgradePrice = 0) => bookingItemOriginTotalPrice.value + upgradePrice
</script>

<style lang="scss" scoped>
.reservation-addition-and-upgrade {
  .readonly {
    @apply cursor-not-allowed;

    :deep(.n-checkbox-box) {
      @apply bg-yellow-20;
      .n-checkbox-box__border {
        @apply !border-yellow-20;
      }
    }
    :deep(.n-radio__dot) {
      box-shadow: inset 0 0 0 1px #ffe7b4; // yellow-20
      &::before {
        @apply bg-yellow-20;
      }
    }
  }
}
</style>
